import React from "react";

// WelcomePage — the introductory page readers see *before* they open
// the book proper.
//
// Discovery flow:
//   - Auto-shown on first sign-in (App.tsx useEffect redirects /
//     → /welcome when localStorage WELCOME_DISMISSED_KEY isn't set).
//   - Always reachable from a "?" button in the slim header, so readers
//     can come back to revisit the rationale anytime — Shlomo's point:
//     this isn't just an onboarding popup, it's reference content.
//
// Source of truth for the Hebrew copy: marketing/welcome-page-v2.md.
// When the wording shifts, edit there first, then port the changes
// down to the <Section> nodes below.
//
// All inline styles, no external CSS dependencies — mirrors ContactPage's
// pattern. Uses the book's warm-brown palette by hex code so it survives
// independent of App.tsx's `styles` block.

// Must stay in sync with the same constant in App.tsx. Bump the version
// suffix when we add new content that previously-dismissed users should
// see again. v2 introduces sections for the new listening-experience
// features (🎧 per-section listen, 📜 auto-scroll, ⏸ exercise pause,
// 🚗 driving safety). v3 adds the 📍 locator + "המקומות שלי" bookmark
// list, so every returning reader should see the page once on their next
// visit.
export const WELCOME_DISMISSED_KEY = "ei_welcome_dismissed_v3";

function markWelcomeDismissed(): void {
  try {
    window.localStorage.setItem(WELCOME_DISMISSED_KEY, "1");
  } catch {
    // ignore — private mode etc. Worst case the auto-show fires once more.
  }
}

const COLORS = {
  bg: "#FAF7F2",
  bg2: "#F3EDE3",
  sand: "#E8DCC8",
  warm: "#C4A882",
  terra: "#A0704A",
  deep: "#5C3D2E",
  text: "#2D1B10",
  muted: "#8B7355",
  accent: "#D4875A",
  white: "#FFFDF9",
};

type SectionProps = {
  icon: string;
  title: string;
  body: React.ReactNode;
};

function Section({ icon, title, body }: SectionProps): React.ReactElement {
  return (
    <div
      style={{
        background: COLORS.white,
        border: `1px solid ${COLORS.sand}`,
        borderRadius: 14,
        padding: "18px 20px",
        marginBottom: 14,
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${COLORS.warm}, ${COLORS.accent})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: "'Frank Ruhl Libre', serif",
            fontWeight: 700,
            fontSize: 18,
            color: COLORS.deep,
            marginBottom: 6,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 14.5,
            lineHeight: 1.65,
            color: COLORS.text,
          }}
        >
          {body}
        </div>
      </div>
    </div>
  );
}

export function WelcomePage(): React.ReactElement {
  return (
    <div
      dir="rtl"
      style={{
        minHeight: "100vh",
        background: COLORS.bg,
        color: COLORS.text,
        fontFamily: "'Heebo', system-ui, sans-serif",
        padding: "32px 16px 80px",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* Hero */}
        <div
          style={{
            background: `linear-gradient(135deg, ${COLORS.deep} 0%, ${COLORS.terra} 60%, ${COLORS.accent} 100%)`,
            borderRadius: 24,
            padding: "36px 28px 32px",
            color: "white",
            marginBottom: 24,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              fontFamily: "'Frank Ruhl Libre', serif",
              fontSize: 13,
              opacity: 0.75,
              letterSpacing: 2,
              marginBottom: 8,
            }}
          >
            ברוכים הבאים
          </div>
          <h1
            style={{
              fontFamily: "'Frank Ruhl Libre', serif",
              fontSize: 30,
              fontWeight: 700,
              lineHeight: 1.25,
              margin: 0,
              marginBottom: 12,
            }}
          >
            שאלת האריה — סדנה בספר
          </h1>
          <div
            style={{
              fontSize: 15,
              opacity: 0.92,
              lineHeight: 1.6,
              fontWeight: 400,
              maxWidth: 560,
            }}
          >
            כמה דקות לפני שאתם פותחים את הספר — כדאי להבין איך הוא בנוי, כי
            הוא לא ספר שקוראים, הוא ספר שעובדים איתו.
          </div>
        </div>

        {/* Sections */}
        <Section
          icon="📖"
          title="קוראים, מקשיבים — והכי טוב: שניהם יחד"
          body={
            <>
              לכל פרק יש <strong>טקסט מלא וגם הקראה בקולי</strong>. בראש כל
              פרק שני כפתורים — <em>קריאה</em> ו-<em>האזנה</em> — לחצו על
              אחד כדי לעבור למצב המתאים. ההקלטה ממשיכה לנגן גם כשהמסך נעול
              ובמכונית.
              <br />
              <strong>וההמלצה החמה שלי:</strong> הקשיבו ועקבו בעיניים בטקסט
              במקביל. הקול מקריא, העיניים עוקבות, וכשאני שואל - עוצרים
              וכותבים. ככה זו כבר לא קריאה של ספר - זו השתתפות בסדנה, הכי
              קרוב למציאות שיש.
            </>
          }
        />

        <Section
          icon="🎧"
          title="לקפוץ ישר לסעיף ספציפי"
          body={
            <>
              במצב קריאה תראו ליד כותרת כל סעיף{" "}
              <strong>אייקון אוזניות 🎧</strong> — לחיצה עליו תפעיל את
              ההאזנה <strong>בדיוק מהסעיף הזה</strong>, בלי להאזין לכל מה
              שהיה לפניו. גם <strong>תפריט תוכן העניינים</strong> (אייקון
              ☰ למעלה משמאל) מאפשר לבחור פרק או תת-סעיף ולקפוץ אליו.
            </>
          }
        />

        <Section
          icon="📜"
          title="המסך זז יחד עם ההקלטה"
          body={
            <>
              במצב האזנה, המסך גולל אוטומטית כך שהטקסט המנוגן תמיד מול
              העיניים. אם זה מפריע — לחצו על{" "}
              <strong>🔒 השאר במקום</strong> בראש הנגן ויעצור. אפשר להפעיל
              שוב באותו כפתור בכל רגע.
            </>
          }
        />

        <Section
          icon="⏸"
          title="תרגיל עם טבלה? הקול עוצר לבד"
          body={
            <>
              כשההקלטה מגיעה לתרגיל מובנה (טבלה או שדות שצריך למלא), הנגן{" "}
              <strong>משהה את עצמו</strong> ומציע: "מלא עכשיו, או המשך
              האזנה". כך לא תפספסו תרגיל בלי משים. תרגילים פתוחים יותר
              (שאלות לחשיבה) ממשיכים כרגיל, מבלי לעצור.
            </>
          }
        />

        <Section
          icon="🚗"
          title="בטיחות בנהיגה — חוק וטעם"
          body={
            <>
              אם אתם נוהגים — <strong>האזנה בלבד</strong>. לא קוראים, לא
              כותבים. החוק הישראלי אוסר על שימוש במסך בזמן נהיגה, וגם
              הבטיחות עצמה מחייבת. בכניסה הראשונה למצב האזנה תקבלו תזכורת
              קצרה לפני שמתחילים.
            </>
          }
        />

        <Section
          icon="✍️"
          title="תיבות כתיבה — לא קישוט"
          body={
            <>
              בכל פרק תמצאו <strong>תיבות כתיבה ותרגילים</strong> — הם
              הסיבה שהספר עובד. רגע של עצירה לכתוב מה שעולה לכם — מצמיד את
              הרעיון לחיים שלכם. אל תדלגו עליהם. גם משפט אחד עוזר.
            </>
          }
        />

        <Section
          icon="💾"
          title="הכל נשמר אוטומטית, גם בין מכשירים"
          body={
            <>
              כל מה שאתם כותבים נשמר אצלכם — וגם בענן בזכות החשבון. תוכלו
              לסגור את הספר באמצע, לפתוח אותו מחר בטלפון אחר, והמילים יחכו
              לכם בדיוק איפה שעצרתם. <strong>אין צורך ללחוץ "שמור"</strong>
              . זה קורה מעצמו.
            </>
          }
        />

        <Section
          icon="📍"
          title="לשמור מקום — ולחזור אליו, או לשתף חבר"
          body={
            <>
              נתקלתם במשפט ששווה לחזור אליו? לחצו על{" "}
              <strong>📍 בכותרת</strong>. תראו באיזה פרק וסעיף אתם, ותוכלו{" "}
              <strong>לשמור את המקום</strong> לרשימה אישית — או{" "}
              <strong>להעתיק קישור</strong> ולשלוח לחבר, שייפתח אצלו בדיוק
              באותה נקודה. את כל המקומות ששמרתם תמצאו תחת{" "}
              <strong>⭐ "המקומות שלי"</strong> בתפריט{" "}
              <strong>☰</strong> — לחיצה אחת מחזירה אתכם לשם. המקום נשמר לפי
              הטקסט עצמו, כך שהוא יישאר נכון גם אם הספר יתעדכן.
            </>
          }
        />

        <Section
          icon="🌳"
          title="קצב — לאט, ולא בבת אחת"
          body={
            <>
              בספר 12 פרקים. אפשר לסיים אותו ב-3 שבועות,{" "}
              <em>אבל זה לא המומלץ</em>. עדיף פרק לשבוע — שיהיה זמן לחיות
              עם הרעיונות, לראות איפה הם נוגעים, ולחזור לכתוב כשמשהו צף.
              הכלים האמיתיים נטמעים לאט.
            </>
          }
        />

        {/* CTA */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 32,
            marginBottom: 16,
          }}
        >
          <a
            href="/"
            onClick={markWelcomeDismissed}
            style={{
              background: COLORS.accent,
              color: "white",
              border: "none",
              borderRadius: 30,
              padding: "14px 36px",
              fontSize: 16,
              fontWeight: 600,
              fontFamily: "'Heebo', sans-serif",
              cursor: "pointer",
              textDecoration: "none",
              boxShadow: "0 4px 16px rgba(160,112,74,0.32)",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>אני מוכן — קחו אותי לספר</span>
            <span style={{ fontSize: 18 }}>←</span>
          </a>
        </div>

        <div
          style={{
            textAlign: "center",
            fontSize: 12.5,
            color: COLORS.muted,
            marginTop: 28,
          }}
        >
          אפשר לחזור לעמוד הזה בכל רגע — כפתור{" "}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 18,
              height: 18,
              borderRadius: "50%",
              background: COLORS.bg2,
              border: `1px solid ${COLORS.sand}`,
              fontSize: 11,
              fontWeight: 700,
              color: COLORS.deep,
              verticalAlign: "middle",
              marginInline: 2,
            }}
          >
            ?
          </span>{" "}
          בכותרת הספר.
        </div>
      </div>
    </div>
  );
}
